import React, { useContext, useEffect, useState, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { WelletContext } from '../../context/WelletContext';
import ContentBottom from '../../components/ContentBottom';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import useForm from '../../helpers/useForm';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import ButtonProccessing, {
  ButtonStatus,
} from '../../components/Common/ButtonProccessing/ButtonProccessing';
import { useDispatch, useSelector } from 'react-redux';
import { setCanRefresh } from '../../actions/appActions';
import { accountValidationRules } from '../../helpers/accountValidationRules';
import { getBankAccount, addAccount } from '../../services/withdrawal/withdrawalService';
import { CircleSpinner } from 'react-spinners-kit';

const AddAccount = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const user = useSelector((state) => state.associates.user);
  const [errorMessage, setErrorMessage] = useState(null);
  const [btnStatus, setBtnStatus] = useState(ButtonStatus.Normal);
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const isUpdateAccount = !history.location.state ? true : false;
  const { address, postalCode, city, state, url, countryCode } = isUpdateAccount ? { } : history.location.state.values;

  const FormAddAccountAR = React.lazy(() => import('./AR/FormAddAccount'));
  const FormAddAccountDO = React.lazy(() => import('./DO/FormAddAccount'));
  const FormAddAccountES = React.lazy(() => import('./ES/FormAddAccount'));
  const FormAddAccountMX = React.lazy(() => import('./MX/FormAddAccount'));
  const FormAddAccountCO = React.lazy(() => import('./CO/FormAddAccount'));
  const FormAddAccountUS = React.lazy(() => import('./US/FormAddAccount'));

  const forms = {
    AR: FormAddAccountAR,
    ES: FormAddAccountES,
    MX: FormAddAccountMX,
    DO: FormAddAccountDO,
    CO: FormAddAccountCO,
    US: FormAddAccountUS
  };

  const fetchAccount = async () => {
    try {
      const response = await getBankAccount(welletContext);
      setAccountInfo(response);
    } catch (error) {
      setErrorMessage(error?.response?.data?.errorDescription);
      console.error(error);
    }
    finally {
      setLoading(false);
    }
  };

  const FormAddAccount = React.useMemo(() => {
    if (countryCode || accountInfo) {
      return forms[countryCode ? countryCode : accountInfo.country];
    }
  }, [countryCode, accountInfo]);

  function validate(values) {
    return accountValidationRules[countryCode]
      ? accountValidationRules[countryCode](values, t)
      : {};
  }

  const formatURL = (url) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const sendForm = async () => {
    try {
      setBtnStatus(ButtonStatus.InProgress);

      let response;

      if (isUpdateAccount) {
        response = await updateAccount();
      } else {
        response = await registerAccount();
      }

      setBtnStatus(ButtonStatus.ProgressFinished);

      const nextUrl = isUpdateAccount ? `/success-add-account` : `/confirm-add-account/?id=${response.data.withdrawal.userWithdrawalMethodId}`;
      setTimeout(() => {
        history.push(nextUrl);
      }, 1000);
    } catch (error) {
      setBtnStatus(ButtonStatus.Normal);
      setErrorMessage(error?.response?.data?.errorDescription);
      console.error(error);
    }
  };

  const registerAccount = async () => {
    const formattedURL = formatURL(url);
    return await welletContext.apis.tickets.post('/withdrawal/accounts', {
      CountryCodeOfResidence: countryCode,
      bankAccount: {
        accountNumber: values.accountNumber,
        routingNumber: values.routingNumber,
        typeAccount: values.typeAccount
      },
      businessInformation: {
        url: formattedURL,
      },
      personalInformation: {
        idNumber: values.IdNumber,
        address,
        postalCode,
        city,
        state,
        url: formattedURL,
      },
    });
  }

  const updateAccount = async () => {
    return await addAccount(welletContext, values.accountNumber, values.routingNumber, values.typeAccount);
  }

  let { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  useEffect(() => {
    if (isUpdateAccount) {
      fetchAccount();
    }
    else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!history.location.state && !user.withdrawalMethod) history.push('/add-personal-information');
    dispatch(setCanRefresh(false));

    return () => {
      dispatch(setCanRefresh(true));
    };
  }, []);

  return (
    <>
      {loading ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header />
          <ContentApp>
            <div className='fw-bold font-medium mt-4'>{t('stripe.addAccounte')}</div>
            <div className='text-secondary mt-1'>{t('stripe.earningsAccount')}</div>
            {FormAddAccount && (
              <Suspense>
                <FormAddAccount values={values} errors={errors} handleChange={handleChange} isUpdateAccount={isUpdateAccount} />
              </Suspense>
            )}
          </ContentApp>
          <ContentBottom>
            {errorMessage && (
              <div className='mt-2'>
                <ErrorBox title={'Error'} description={errorMessage} />
              </div>
            )}
            <ButtonProccessing
              status={btnStatus}
              text={t('continue')}
              size='large'
              className={`mt-5`}
              type='submit'
              onClick={() => handleSubmit()}
              arrow
              color='#000'
              backgroundColor='var(--color-primary)'
              iconColor='#000'
            />
          </ContentBottom>
        </>
      )}
    </>
  );
};

export default AddAccount;
